import { usePriceContext } from '@ecomm/data-price'
import type { DynamicGift, GiftItemDTO } from '@ecomm/data-simplisafe-api'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/function'
import { useMemo } from 'react'

type Props = {
  readonly defaultBundleSku: string
  readonly hasMonitoring: boolean
  readonly discountedPrice: number
}
/**
 * - If the BMSM v3 experiment is enabled (monitoring is selected and dynamic gifts exist for the SKU/price),
 *   returns:
 *     - dynamicGifts: all dynamic gifts for the current price threshold
 *     - dynamicGiftLineItems: mapped line items for each dynamic gift, with safe defaults
 *   - If the experiment is not enabled, returns an empty arrays for dynamicGiftLineItems.
 */
export const useGetDynamicGifts = ({
  defaultBundleSku,
  hasMonitoring,
  discountedPrice
}: Props) => {
  const { getDynamicGiftsThresholds, getDynamicGift } = usePriceContext()

  const dynamicGifts = getDynamicGiftsThresholds(
    defaultBundleSku,
    discountedPrice
  )

  const isBMSMv3Enabled = hasMonitoring && dynamicGifts.length > 0

  const dynamicGiftsByTotal = pipe(
    getDynamicGift(discountedPrice, defaultBundleSku),
    O.map(gift => [gift]),
    O.getOrElse<readonly DynamicGift[]>(() => [])
  )

  const dynamicGiftLineItems = useMemo(
    () =>
      dynamicGiftsByTotal.map<GiftItemDTO>(gift => ({
        sku: gift.sku,
        title: gift.name,
        quantity: null,
        value: null
      })),
    [dynamicGiftsByTotal]
  )

  return {
    dynamicGifts,
    dynamicGiftLineItems: isBMSMv3Enabled ? dynamicGiftLineItems : []
  }
}
